// LeadGenBotSettings.js - Updated to load only QNABotContent
import React, { useEffect, useState } from "react";
import AlertPopup from "../../../AlertPopup";
import LoaderComp from "../../../LoaderComp";
import styles from "./BotSettings.module.css";
import ThemeCustomizer from "../../../NewCreateAppF/ThemeCustomizer";
import QNABotContent from "../../../app_components/QNABotContent"; // Import directly
import Modal from "../../../Modal";
import modalContentSty from "./ModalContent.module.css";
import LeadGeneration from "../LeadGeneration/LeadGeneration";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
// ❌ REMOVE this line - token should be retrieved dynamically
// const token = localStorage.getItem("token");

// Helper function to get fresh token
const getToken = () => localStorage.getItem("token");

// Constants
const CORE_KEYS = [
  "app_name",
  "ai_agent_bot_enabled",
  "live_agent_enabled",
  "allow_nested",
  "dont_show_chat_box_at_logout",
  "exclude_paths",
  "app_id",
  "auth_key",
  "chat_settings",
  "enabled_bot_content",
  "feature",
  "region",
  "role",
  "settings",
  "tenant_id",
  "timestamp",
  "tenant_info",
  "isTraining",
];

const BOT_CONTENT_KEYS = ["FAQ", "WELCOME_MSG", "OFFLINE_MSG", "kb_id", "render_root_as_buttons"];

export default function LeadGenBotSettings({
  appData,
  tenantPlan,
  onAppDataPatch,
}) {
  console.log("in LeadGenBotSettings", JSON.stringify(appData));
  const [initialData, setInitialData] = useState(null);

  const [botContent, setBotContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [localChatSettings, setLocalChatSettings] = useState(
    appData.chat_settings,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayloads, setPendingUpdatePayloads] = useState({
    payload1: null,
    payload2: null,
  });

  // Helper to check if the tree has incomplete questions (the red border cases)
  const hasIncompleteNodes = (node) => {
    if (!node) return false;

    // A field inside a form is always valid
    if (node.type === "field_type") return false;

    // If it's a form, it doesn't need options/subQuestions
    if (node.type === "form_type") return false;

    // For Multiple Choice nodes, they MUST have either options or subQuestions
    const hasOptions = node.options && node.options.length > 0;
    const hasSubQuestions = node.subQuestions && node.subQuestions.length > 0;

    if (!hasOptions && !hasSubQuestions) {
      return true; // Incomplete!
    }

    // Recursively check children
    if (hasSubQuestions) {
      for (const child of node.subQuestions) {
        if (hasIncompleteNodes(child)) return true;
      }
    }

    return false;
  };
  console.log("showConfirmModal", showConfirmModal);

  const hasIncomplete = botContent?.FAQ ? hasIncompleteNodes(botContent.FAQ) : false;


  const hasChanges = () => {
    if (!initialData || !botContent) return false;

    const stableStringify = (obj) => {
      if (!obj || typeof obj !== "object") return JSON.stringify(obj);

      if (Array.isArray(obj)) {
        return JSON.stringify(obj.map((item) => stableStringify(item)));
      }

      const sortedObj = {};
      Object.keys(obj)
        .sort()
        .forEach((key) => {
          sortedObj[key] = stableStringify(obj[key]);
        });
      return JSON.stringify(sortedObj);
    };

    const botContentChanged = BOT_CONTENT_KEYS.some((key) => {
      if (
        initialData.botContent[key] === undefined ||
        botContent[key] === undefined
      ) {
        return false;
      }

      if (key === "FAQ") {
        return (
          stableStringify(initialData.botContent[key]) !==
          stableStringify(botContent[key])
        );
      }

      return (
        JSON.stringify(initialData.botContent[key]) !==
        JSON.stringify(botContent[key])
      );
    });

    const coreChanged = CORE_KEYS.some((key) => {
      if (key === "chat_settings") return false;

      if (
        initialData.botContent[key] === undefined ||
        botContent[key] === undefined
      ) {
        return false;
      }
      return (
        JSON.stringify(initialData.botContent[key]) !==
        JSON.stringify(botContent[key])
      );
    });

    const chatSettingsChanged =
      stableStringify(initialData.chatSettings) !==
      stableStringify(localChatSettings);

    return botContentChanged || coreChanged || chatSettingsChanged;
  };

  const confirmUpdate = async (confirmed) => {
    setShowConfirmModal(false);

    if (!confirmed) {
      showAlert("info", "Update cancelled.");
      return;
    }

    const { payload1, payload2 } = pendingUpdatePayloads;

    if (!payload1 || !payload2) {
      showAlert("error", "Update data not found");
      return;
    }

    setUpdating(true);

    try {
      console.log("Starting update process for Lead Gen Bot");

      // ✅ Get fresh token at the time of API call
      const token = getToken();

      if (!token) {
        showAlert(
          "error",
          "Authentication token not found. Please log in again.",
        );
        setUpdating(false);
        return;
      }

      // -----------------------------
      // 1️⃣ UPDATE APP (payload1)
      // -----------------------------
      const region_prefix = appData.region.toLowerCase();
      const updateAppResponse = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/update_app?act_type=tenant&app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token, // ✅ Using fresh token
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload1),
        },
      );

      const updateAppData = await updateAppResponse.json();
      console.log("update_app response", updateAppData);

      if (!updateAppResponse.ok) {
        showAlert("error", "Failed to update app configuration");
        setUpdating(false);
        return;
      }

      // -----------------------------
      // 2️⃣ UPDATE BOT CONTENT (payload2)
      // -----------------------------
      const region = appData.region.toLowerCase();

      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/update_bot_content?app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token, // ✅ Using fresh token
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload2),
        },
      );

      const botContentRes = await response.json();

      if (response.ok) {
        setInitialData({
          botContent: { ...botContent },
          chatSettings: { ...localChatSettings },
        });

        showAlert("success", "Lead Gen Bot content updated successfully!");

        // ─────────────────────────────────────────────
        // Lift the persisted bot flag up to AppDetailPage
        // so Credentials sees fresh state immediately.
        // ─────────────────────────────────────────────
        if (onAppDataPatch) {
          onAppDataPatch({
            enabled_bot_content: botContent.enabled_bot_content,
          });
        }

        // Trigger process hard reset if available
        if (window.MyPackage?.processHardReset) {
          window.MyPackage.processHardReset(
            token,
            appData.app_name,
            appData.region.toLowerCase(),
          );
        }
      } else {
        showAlert("error", "Failed to update bot content");
      }
    } catch (error) {
      console.error("Update failed:", error);
      showAlert("error", "Update failed: " + error.message);
    } finally {
      setUpdating(false);
      setPendingUpdatePayloads({ payload1: null, payload2: null });
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  // ========== VALIDATION FUNCTION FOR CHATBOT STATES ==========
  const checkOtherChatbotsEnabled = () => {
    // Check if AI Chat is enabled (from botContent)
    const isAiAgentEnabled = botContent?.ai_agent_bot_enabled === true;
    // Check if Live Agent Bot is enabled (from botContent)
    const isLiveAgentEnabled = botContent?.live_agent_enabled === true;

    return {
      aiAgentEnabled: isAiAgentEnabled,
      liveAgentEnabled: isLiveAgentEnabled,
      anyOtherEnabled: isAiAgentEnabled || isLiveAgentEnabled,
    };
  };
  // ========== END VALIDATION FUNCTION ==========

  useEffect(() => {
    if (!appData?.tenant_id || !appData?.app_name) return;

    const fetchBotContent = async () => {
      setLoading(true);

      try {
        const region = appData.region.toLowerCase();

        // Note: This API uses x-api-key, not token - so no change needed here
        const url = `https://${region}.autobot2.${backend_api_base_url}/get_bot_content?app_name=${appData.app_name}&tenant_id=${appData.tenant_id}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "x-api-key": "bGVnYWwxMjNfX1NFVFJBVE9SX190ZXN0ZGRk",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch bot content");

        const data = await response.json();
        const fetchedBotContent = {
          ...data?.bot_payload,
          ...appData,
          FAQ: data?.bot_payload?.FAQ || {
            statement: "",
            answers: [],
            subQuestions: [],
          },
          WELCOME_MSG: data?.bot_payload?.WELCOME_MSG || "",
          OFFLINE_MSG: data?.bot_payload?.OFFLINE_MSG || "",
        };

        console.log("Fetched bot content for LeadGen:", fetchedBotContent);
        setBotContent(fetchedBotContent);
        setInitialData({
          botContent: { ...fetchedBotContent },
          chatSettings: { ...appData.chat_settings },
        });
      } catch (err) {
        showAlert("error", "❌ " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBotContent();
  }, [appData]);

  const handleUpdate = async () => {
    console.log("botContent", botContent);

    if (!botContent) {
      showAlert("error", "No bot content to update");
      return;
    }

    // ========== VALIDATION: Check if trying to disable all chatbots ==========
    const isLeadGenEnabled = botContent.enabled_bot_content;
    const { aiAgentEnabled, liveAgentEnabled, anyOtherEnabled } =
      checkOtherChatbotsEnabled();

    // If LeadGen Bot is being disabled (or is already disabled) AND no other chatbots are enabled
    if (!isLeadGenEnabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable QnA (LeadGen) Bot! You need at least one chatbot enabled. Please enable either AI Chat or Live Agent Bot before disabling QnA Bot.",
      );
      return;
    }

    // Also check if we're about to save with LeadGen disabled and no other bots enabled
    // (This handles the case where LeadGen is currently enabled but we're about to disable it)
    const willBeDisabled = !isLeadGenEnabled;
    if (willBeDisabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable QnA (LeadGen) Bot! You need at least one chatbot enabled. Currently, both AI Chat and Live Agent Bot are disabled. Please enable one of them first.",
      );
      return;
    }
    // ========== END VALIDATION ==========

    // Split into two payloads
    const payload1 = {};
    const payload2 = {};

    Object.keys(botContent).forEach((key) => {
      if (CORE_KEYS.includes(key)) payload1[key] = botContent[key];
      if (BOT_CONTENT_KEYS.includes(key)) payload2[key] = botContent[key];
    });

    console.log("Payload 1 (Core Config):", payload1);
    console.log("Payload 2 (Bot Content):", payload2);

    // Store payloads and show confirmation modal
    setPendingUpdatePayloads({ payload1, payload2 });
    setShowConfirmModal(true);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const handleModalClose = () => {
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className={styles.centered}>
        <LoaderComp message="Loading Lead Gen Bot Content" />
      </div>
    );
  }

  if (!botContent) {
    return <div>Error: Unable to load bot content</div>;
  }

  console.log(
    "Rendering LeadGenBotSettings with enabled_bot_content:",
    botContent.enabled_bot_content,
  );

  return (
    <>
      <LeadGeneration appData={appData} />

      <div className={styles.botContent_wrap}>
        <h2 className={styles.title}>
          Interactive FAQ with Lead Generation Settings
        </h2>

        {/* Only load QNABotContent */}
        <QNABotContent setBotContent={setBotContent} botContent={botContent} />

        {!botContent.enabled_bot_content && (
          <div className={styles.infos}>
            ⚠️ Please enable FAQ bot to configure bot content
          </div>
        )}

                <button
          onClick={handleUpdate}
          disabled={updating || !botContent || loading || !hasChanges() || hasIncomplete}
          className={`${styles.updateBtn} ${updating ? styles.disabled : ""}`}
          title={hasIncomplete ? "Please complete all open questions before updating." : ""}
        >
          {updating ? "Updating..." : "Update"}
        </button>

        {alert.show && (
          <AlertPopup
            type={alert.type}
            message={alert.message}
            onClose={handleCloseAlert}
          />
        )}

        {/* Confirmation Modal */}
        <Modal
          isOpen={showConfirmModal}
          onClose={handleModalClose}
          hideCloseButton={true}
          title="Confirmation Required"
        >
          <div className={modalContentSty.modalContent}>
            <div className={modalContentSty.modalContentWrap}>
              <div className={modalContentSty.modalContentIc}>⚠️</div>
              <p className={modalContentSty.modalMessage}>
                All the clients would be rebooted! Do you want to continue?
              </p>
            </div>
            <div className={modalContentSty.modalActions}>
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
                onClick={() => confirmUpdate(false)}
              >
                Cancel
              </button>
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
                onClick={() => confirmUpdate(true)}
              >
                Ok
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}